import { useCallback, useEffect, useState } from 'react';
import { Fingerprint, ScanFace, ShieldCheck, Check } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/core/supabase';
import { invokeFunction } from '@/core/invokeFunction';
import { useSession } from '@/core/session';
import {
  biometricStatus, enableBiometric, disableBiometric, type BiometricStatus,
} from '@/core/biometric';
import { Card, CardHeader, Button } from './ui';
import { Spinner } from './Loader';
import { useT } from '@/core/i18n';
import { errorMessage } from '@/core/errorMessage';

/* =============================================================================
 * Biometric sign-in setting
 * -----------------------------------------------------------------------------
 * Shared, because both audiences need it and neither has a settings screen of
 * its own worth building for one toggle: an administrator finds it under
 * Appearance, a worker on the front page of his portal.
 *
 * Renders nothing at all in a browser, or on a handset with no reader. A
 * control that explains why it cannot work is worse than no control — it puts
 * a dead end in front of somebody who was not looking for one.
 * ========================================================================== */

export function BiometricCard() {
  const t = useT();
  const { profile } = useSession();
  const [status, setStatus] = useState<BiometricStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus(await biometricStatus());
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  if (!status?.available) return null;

  // Face-only devices get the face icon; everything else gets the fingerprint,
  // including "fingerprint or face" where a single glyph has to stand for both.
  const Icon = status.label === 'face' ? ScanFace : Fingerprint;
  const noun = status.label;

  async function enable() {
    setBusy(true);
    setError(null);
    try {
      // Ask the server for a secret that belongs to this device.
      //
      // Not the session's refresh token, which was the earlier design and could
      // not work: Supabase rotates those on every background refresh, so the
      // stored copy was dead within the hour. This secret never rotates, and
      // the server keeps only its hash.
      const { data } = await supabase.auth.getSession();
      const email = data.session?.user.email ?? profile?.email ?? '';

      if (!data.session) {
        setError('Sign in again before turning this on.');
        return;
      }

      const enrolment = await invokeFunction<{ secret?: string }>('biometric-auth', {
        body: {
          action: 'enrol',
          platform: Capacitor.getPlatform(),
          label: t('bc.onThisDevice', { what: status!.label }),
        },
      });

      if (!enrolment?.secret) throw new Error('The server did not return a device key.');

      await enableBiometric(email, enrolment.secret, status!.label);
      await refresh();
    } catch (caught) {
      const message = errorMessage(caught);
      // Cancelling the prompt is a decision, not an error worth reporting back.
      if (!/cancel/i.test(message)) setError(message);
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setError(null);
    await disableBiometric();
    await refresh();
    setBusy(false);
  }

  return (
    <Card>
      <CardHeader
        title={t('bc.signInWith', { what: noun })}
        subtitle={t('bc.thisDeviceOnly')}
      />

      <div className="flex items-start gap-3.5 mt-4">
        <span className="shrink-0 w-10 h-10 rounded-card bg-brand-subtle text-brand grid place-items-center">
          <Icon size={20} />
        </span>

        <div className="min-w-0 flex-1">
          {status.enrolled ? (
            <p className="text-[13px] flex items-center gap-1.5 text-ok font-medium">
              <Check size={15} className="shrink-0" />
              {t('bc.turnedOn')}
            </p>
          ) : (
            <p className="text-[13px] font-medium">{t('bc.notSetUp')}</p>
          )}

          <p className="text-[12.5px] text-muted mt-1.5 leading-relaxed max-w-prose">
            Your {noun} never leaves this phone and is never sent to us. The
            phone checks it against what you enrolled in Android, and only then
            unlocks the sign-in it is holding for you.
          </p>

          {/* Android chooses which biometric to offer, not the app. Saying so
              answers "why does it only ask for my fingerprint" without anyone
              having to ask. */}
          <p className="text-[11.5px] text-subtle mt-2 leading-relaxed max-w-prose">
            {status.label === 'fingerprint or face'
              ? 'Android will offer whichever of the two you use.'
              : status.label === 'fingerprint'
                ? 'Only a fingerprint is enrolled on this phone. Add face unlock in Android settings and it will be offered here too, if this handset supports it securely enough.'
                : status.label === 'face'
                  ? 'Only face unlock is enrolled on this phone.'
                  : 'Android decides which method to offer, based on what you have enrolled.'}
          </p>

          {error && (
            <p role="alert" className="text-[12.5px] text-danger bg-danger-subtle rounded-card px-3 py-2 mt-3 leading-snug">
              {error}
            </p>
          )}

          <div className="mt-3.5">
            {status.enrolled ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => void disable()}
                icon={busy ? <Spinner size={13} /> : undefined}
              >
                {t('bc.turnOff')}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => void enable()}
                icon={busy ? <Spinner size={13} /> : <ShieldCheck size={14} />}
              >
                {busy ? t('bc.confirming') : t('bc.turnOn', { what: noun })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
